import { oauthConfig, REVOKE_URL } from "../config/oauth.config";

import { ACTIVE_TENANT_COOKIE, BASE_DOMAIN, OAUTH_STATE_KEY } from "./constants";
import type { OAuthState } from "./types";

// Allows only same-origin relative paths.
// Rejects: external URLs, protocol-relative //, data:, javascript:, or malformed strings.
const SAFE_REDIRECT_RE = /^\/[a-zA-Z0-9/_\-?=&#]*$/;

export function validateRedirect(raw: unknown): string | null {
  if (typeof raw !== "string" || !raw) return null;
  if (raw.startsWith("http") || raw.startsWith("//")) return null;
  if (!SAFE_REDIRECT_RE.test(raw)) return null;
  return raw;
}

export function storeAuthState(state: Pick<OAuthState, "redirect" | "tenant">): void {
  const full: OAuthState = {
    ...state,
    nonce: crypto.randomUUID(),
    v: 1,
  };
  sessionStorage.setItem(OAUTH_STATE_KEY, JSON.stringify(full));
}

// Reads and removes the stored OAuth state. Returns null on parse failure or version mismatch.
export function consumeAuthState(): OAuthState | null {
  const raw = sessionStorage.getItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as OAuthState;
    if (parsed.v !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function extractTenant(hostname: string, baseDomain: string): string | null {
  if (!hostname.endsWith(baseDomain)) return null;
  const withoutBase = hostname.slice(0, hostname.length - baseDomain.length);
  const sub = withoutBase.replace(/\.$/, "");
  return sub || null;
}

export function getCurrentTenant(): string | null {
  if (typeof window === "undefined") return null;
  return extractTenant(window.location.hostname, BASE_DOMAIN);
}

// Builds an absolute URL back to the tenant subdomain for cross-origin post-auth navigation.
export function buildTenantUrl(tenant: string, path: string): string {
  const { protocol, port } = window.location;
  const portSuffix = port ? `:${port}` : "";
  return `${protocol}//${tenant}.${BASE_DOMAIN}${portSuffix}${path}`;
}

// Maps a raw OAuth/library error string to a normalized message suitable for the UI.
export function normalizeAuthError(raw: unknown): string {
  if (typeof raw !== "string" || !raw) return "An unexpected authentication error occurred.";
  const lower = raw.toLowerCase();
  if (lower.includes("access_denied")) return "Access was denied. Please try again.";
  if (lower.includes("invalid_client")) return "OAuth client configuration error.";
  if (lower.includes("invalid_grant")) return "Authorization code expired or already used.";

  return raw;
}

// Cookie helpers for single-client session enforcement (LOA-XXX).
// The cookie is set on the root domain so it's shared across all tenant subdomains.

export function setActiveTenantCookie(tenant: string): void {
  const domain = `.${BASE_DOMAIN}`;
  // Only add Secure flag on HTTPS - localhost typically uses HTTP
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${ACTIVE_TENANT_COOKIE}=${tenant}; domain=${domain}; path=/; SameSite=Lax${secure}`;
}

export function getActiveTenantCookie(): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${ACTIVE_TENANT_COOKIE}=([^;]*)`)
  );

  return match ? decodeURIComponent(match[1]) : null;
}

export function clearActiveTenantCookie(): void {
  const domain = `.${BASE_DOMAIN}`;
  document.cookie = `${ACTIVE_TENANT_COOKIE}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

type RevocableTokenType = "access_token" | "refresh_token";

function readRocpValue(key: string): string | null {
  const raw = localStorage.getItem(key);

  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as string;

    return parsed || null;
  } catch {
    return null;
  }
}

/**
 * LOA-1613: the access token currently stored for this origin (shared across
 * tabs via localStorage). Used by the 401 handler to tell a dead request for
 * THIS session apart from a stale one for a session another tab has already
 * replaced with a fresh login.
 */
export function getStoredAccessToken(): string | null {
  return readRocpValue("ROCP_token");
}

async function revokeOne(token: string, tokenTypeHint: RevocableTokenType): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 2000);

  try {
    const response = await fetch(REVOKE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        token,
        token_type_hint: tokenTypeHint,
        client_id: oauthConfig.clientId,
      }),
      signal: controller.signal,
      // Both call sites fire this immediately before navigating away
      // (window.location.replace/href), which would otherwise cancel this
      // request mid-flight as the old document unloads.
      keepalive: true,
    });

    // fetch only rejects on a transport failure — an HTTP error status
    // still resolves normally. RFC 7009 returns 200 even for an unknown or
    // already-revoked token (anti-scanning), so a non-ok response here is a
    // genuine failure (bad client_id, 5xx, …), not that expected no-op.
    if (!response.ok) {
      console.warn(
        `[revokeTokens] ${tokenTypeHint} revocation returned ${response.status}; ` +
          "the token may still be usable until it naturally expires.",
      );
    }
  } catch (error) {
    // Network error, timeout/abort, or CORS failure. Logout must never get
    // stuck on this — clearing local state and redirecting is all that
    // matters from here — but it must not be silent, since the token being
    // revoked is about to be erased from local storage either way.
    console.warn(
      `[revokeTokens] ${tokenTypeHint} revocation failed; ` +
        "the token may still be usable until it naturally expires.",
      error,
    );
  } finally {
    clearTimeout(timer);
  }
}

/**
 * LOA-1613: best-effort RFC 7009 revocation of THIS session's own access and
 * refresh tokens, via django-oauth-toolkit's standard `/o/revoke_token/`
 * endpoint. Revokes only these two tokens, not every token the user holds —
 * unlike the backend's same-tenant logout view, which (by design, see
 * apps/authentication/views.py) only flushes the Django session and leaves
 * OAuth tokens live, precisely so a plain logout doesn't sign the user out
 * of other devices/tabs.
 *
 * MUST be called before clearClientAuthState() wipes ROCP_* from
 * localStorage, or there is nothing left here to revoke.
 *
 * Never throws and never blocks logout: a slow/failed network request is
 * bounded by a short per-call timeout, so logout always proceeds to clear
 * local state and redirect regardless of whether revocation actually
 * succeeded server-side. A failure (HTTP error or transport failure) is
 * logged via console.warn rather than silently discarded, since the token
 * it was trying to revoke is about to become unobservable once local
 * storage is cleared.
 */
export async function revokeTokens(): Promise<void> {
  const accessToken = readRocpValue("ROCP_token");
  const refreshToken = readRocpValue("ROCP_refreshToken");

  await Promise.allSettled(
    [
      accessToken ? revokeOne(accessToken, "access_token") : null,
      refreshToken ? revokeOne(refreshToken, "refresh_token") : null,
    ].filter((call): call is Promise<void> => call !== null),
  );
}

/**
 * Purge every client-side OAuth credential for this origin: the
 * react-oauth2-code-pkce tokens in localStorage (shared across tabs) and the
 * pending OAuth flow state in sessionStorage.
 *
 * Must run on every path that invalidates the session — including the 401
 * handler, since a server-side revocation (cross-tenant logout) leaves
 * `ROCP_token` behind, and `hasStoredOAuthToken()` would then hold the
 * protected layout on its initializing spinner instead of letting RouteGuard
 * redirect to login.
 *
 * CRITICAL: Do NOT call the library's logOut() instead — it writes NEW stale
 * PKCE state that breaks a fresh login in another tab.
 */
export function clearClientAuthState(): void {
  const ropKeys = Object.keys(localStorage).filter((k) => k.startsWith("ROCP_"));

  ropKeys.forEach((k) => localStorage.removeItem(k));
  sessionStorage.removeItem(OAUTH_STATE_KEY);
}

/**
 * Check if a token exists in localStorage from the react-oauth2-code-pkce library.
 *
 * This is a workaround for timing issues where the library hasn't yet read the
 * token from localStorage but the user is actually authenticated. Used to
 * prevent premature redirects to login when opening a new tab with a direct URL.
 *
 * The library uses "ROCP_" prefix for its storage keys and stores values as JSON.
 */
export function hasStoredOAuthToken(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  // react-oauth2-code-pkce stores tokens with ROCP_ prefix as JSON strings
  // Using localStorage (shared across tabs) per oauth.config.ts storage setting
  const token = localStorage.getItem("ROCP_token");
  const idToken = localStorage.getItem("ROCP_idToken");

  // Values are JSON-stringified, so '""' (empty string) or 'null' are invalid
  // A valid token looks like '"abc123..."' (quoted string with content)
  const hasToken = token !== null && token !== '""' && token !== "null" && token.length > 2;
  const hasIdToken = idToken !== null && idToken !== '""' && idToken !== "null";

  return hasToken || hasIdToken;
}
